import { useTranslation } from 'react-i18next';
import { convertSecondsToTime } from 'utils/timeHelper';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { ActionTypes } from 'infrastructure/enums/action-types';
import { Routes } from 'router/routes';
import { Link } from 'react-router-dom';

import ActionCell from './action-cell';
import s from './styles.module.scss';

import type { ColumnsType } from 'antd/es/table';
import type { ActionTimeManagementItem } from 'infrastructure/hooks/action-time-management/types';

interface IProps {
  billingMonth: number;
  billingYear: number;
  page: number;
}

export const useColumns = ({
  billingMonth,
  billingYear,
  page,
}: IProps): ColumnsType<ActionTimeManagementItem> => {
  const { t } = useTranslation();

  return [
    {
      title: '№',
      width: '1%',
      align: 'center',
      render: (_, record, idx) => {
        const itemIndexPrefix = (page - 1) * 100;
        return itemIndexPrefix + idx + 1;
      },
    },
    {
      title: t('actionTimesManagement.patient'),
      width: '15%',
      render: (_, record) => (
        <Link to={Routes.patientGuid(record.patientGuid)} className={s.link}>
          {record.patientName}
        </Link>
      ),
    },
    {
      title: t('actionTimesManagement.source'),
      width: '5%',
      render: (_, record) => record.source.toUpperCase(),
    },
    {
      title: t('actionTimesManagement.author'),
      width: '15%',
      render: (_, record) => record.authorName,
    },
    {
      title: t('actionTimesManagement.actionType'),
      width: '10%',
      render: (_, record) => {
        if (
          record.actionType === ActionTypes.PatientComment ||
          record.actionType === ActionTypes.CCMPatientCallNotes
        ) {
          return t(`actionTypes.general-comments`);
        }
        if (record.actionType === ActionTypes.CCMPatientNotes) {
          return t(`actionTypes.clinical-notes`);
        }
        return t(`actionTypes.${record.actionType}`);
      },
    },
    {
      title: t('labels.time'),
      width: '10%',
      render: (_, record) => convertSecondsToTime(record.time),
    },
    {
      title: t('labels.createdAt'),
      width: '8%',
      render: (_, record) =>
        DateFormatter({
          date: record.createdAt,
          format: DateFormats['MM-dd-yy | h:mm:ss a'],
        }),
    },
    {
      title: t('labels.action'),
      align: 'center',
      width: '2%',
      render: (_, record) => {
        const currentYear = new Date().getFullYear();
        const currenMonth = new Date().getMonth() + 1;

        const disabled =
          currenMonth !== billingMonth || currentYear !== billingYear;

        return <ActionCell record={record} disabled={disabled} />;
      },
    },
  ];
};
